import React, { useEffect, useState, useMemo } from 'react';
import '../components/layout/AppPage.css';
import './InventoryDashboard.css';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Select,
  Dropdown,
  Typography,
  Collapse,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  WarningOutlined,
  BarChartOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { getStockBarShares } from '../utils/inventoryStockBar';
import ProductDimensionsStack from '../components/products/ProductDimensionsStack';
import { Inventory, InventoryBatch, InventoryMovement } from '../types/pz';
import { canManageInventory } from '../modules/inventory/inventoryAccessPolicy';
import { fetchBatchEarmarks, BatchEarmark } from '../modules/orders/api/orderEarmarksApi';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { fetchInventoryList, fetchInventoryVariantDetail } from '../modules/inventory/api/inventoryApi';
import { getInventoryStockStatus } from '../dto/inventory';
import {
  formatQuantitySet,
  hasPersistedQuantitySet,
  quantitySetFromFields,
} from '../utils/commercialQuantity';
import { formatCategoryPath } from '../modules/products/category/categoryHierarchy';

const { Option } = Select;
const { Text } = Typography;

const EMPTY_QTY = '—';

// CSS-var bridge for the per-category accent mark: the colour is category
// data (not a theme value), so it travels as a custom property and the
// .inventory-dashboard__category-mark class stays the single paint owner.
const CATEGORY_MARK_VAR = '--id-category-mark';

type InventoryWithDetails = Inventory;

interface InventoryFilters {
  search: string;
  status: string | null;
  lowStock: boolean;
}

interface InventorySummary {
  totalItems: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
}

interface InventoryCategoryGroup {
  key: string;
  name: string;
  color: string | null;
  rows: InventoryWithDetails[];
  inStockCount: number;
  outOfStockCount: number;
  inTransitCount: number;
}

interface InventoryVariantDetailState {
  loading: boolean;
  error: string | null;
  batches: InventoryBatch[];
  movements: InventoryMovement[];
}

const DEFAULT_FILTERS: InventoryFilters = {
  search: '',
  status: null,
  lowStock: false,
};

type StockStatus = 'out' | 'low' | 'in';

const hasPositiveQuantitySet = (record: object | null | undefined, prefix: string) => (
  Object.values(quantitySetFromFields(record as Record<string, unknown> | undefined, prefix))
    .some((value) => value !== null && value > 0)
);

const deriveStockStatus = (inv: InventoryWithDetails): StockStatus => {
  const status = getInventoryStockStatus(inv);
  if (status === 'OUT_OF_STOCK') return 'out';
  if (status === 'LOW_STOCK') return 'low';
  return 'in';
};

// UI-side tokens kept on the Select (and their existing i18n labels) map onto
// the derived status above rather than the unreliable persisted column.
const STATUS_FILTER_TO_DERIVED: Record<string, StockStatus> = {
  ACTIVE: 'in',
  LOW_STOCK: 'low',
  OUT_OF_STOCK: 'out',
};

const InventoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();

  const [inventory, setInventory] = useState<InventoryWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<InventoryFilters>(DEFAULT_FILTERS);
  const [summary, setSummary] = useState<InventorySummary>({
    totalItems: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [variantDetails, setVariantDetails] = useState<Record<string, InventoryVariantDetailState>>({});
  // batchEarmarks: keyed by batchId (batch.id), loaded on demand per batch row
  const [batchEarmarks, setBatchEarmarks] = useState<Record<string, BatchEarmark[]>>({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEdit = canManageInventory(authService.getCurrentUser());
  const canViewPurchasePrices = authService.canViewSupplierPurchasePrices();
  const canViewBatchProfitability = authService.canViewFinancialReports();

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const inventoryData = await fetchInventoryList();
      setInventory(inventoryData);

      // Calculate summary
      const totalValue = canViewPurchasePrices
        ? inventoryData.reduce((sum: number, inv: InventoryWithDetails) => sum + (inv.totalValuePLN || 0), 0)
        : 0;
      const lowStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        deriveStockStatus(inv) === 'low').length;
      const outOfStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        deriveStockStatus(inv) === 'out').length;

      setSummary({
        totalItems: inventoryData.length,
        totalValue,
        lowStockCount,
        outOfStockCount,
      });

      // Returning to an already-expanded row must refresh the same atomic
      // detail read model as the aggregate. Otherwise a PZ/WZ completed in a
      // second tab updates the headline while leaving old batch history open.
      await Promise.all(expandedRowKeys.map((variantId) => fetchVariantDetails(variantId)));
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchInventory);

  const fetchVariantDetails = async (variantId: string) => {
    setVariantDetails(prev => ({
      ...prev,
      [variantId]: {
        loading: true,
        error: null,
        batches: prev[variantId]?.batches || [],
        movements: prev[variantId]?.movements || [],
      },
    }));

    try {
      const detail = await fetchInventoryVariantDetail(variantId);
      setVariantDetails(prev => ({
        ...prev,
        [variantId]: {
          loading: false,
          error: null,
          batches: detail.batches,
          movements: detail.recentMovements,
        },
      }));
    } catch (error: any) {
      console.error('Error fetching inventory variant detail:', error);
      setVariantDetails(prev => ({
        ...prev,
        [variantId]: {
          loading: false,
          error: getApiErrorMessage(error, t, 'inventory:error.detailsFetchFailed'),
          batches: [],
          movements: [],
        },
      }));
    }
  };

  const fetchEarmarksForBatch = async (batchId: string) => {
    if (batchEarmarks[batchId] !== undefined) return; // already loaded
    try {
      const earmarks = await fetchBatchEarmarks(batchId);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: earmarks }));
    } catch (error: any) {
      console.error('Error fetching batch earmarks:', error);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpand = (expanded: boolean, record: InventoryWithDetails) => {
    const variantId = record.productVariantId;
    if (expanded) {
      setExpandedRowKeys(current => current.includes(variantId) ? current : [...current, variantId]);
      if (!variantDetails[variantId] || variantDetails[variantId].error) {
        void fetchVariantDetails(variantId);
      }
    } else {
      setExpandedRowKeys(current => current.filter(key => key !== variantId));
    }
  };

  const filteredInventory = useMemo(() => {
    return inventory.filter(inv => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesVariant =
          inv.productVariant?.sku.toLowerCase().includes(searchLower) ||
          inv.productVariant?.variantName.toLowerCase().includes(searchLower);
        const matchesProduct =
          inv.productVariant?.product?.code.toLowerCase().includes(searchLower) ||
          inv.productVariant?.product?.name.toLowerCase().includes(searchLower);
        if (!matchesVariant && !matchesProduct) return false;
      }

      // Status filter — derived from quantities, not the unreliable persisted
      // Inventory.status column (see deriveStockStatus above).
      if (filters.status) {
        const targetStatus = STATUS_FILTER_TO_DERIVED[filters.status];
        if (targetStatus && deriveStockStatus(inv) !== targetStatus) return false;
      }

      // Low stock filter — same derivation, so it always agrees with the
      // "Niski stan" KPI count and the status Select's LOW_STOCK option.
      if (filters.lowStock && deriveStockStatus(inv) !== 'low') return false;

      return true;
    });
  }, [inventory, filters]);

  const groupedInventory = useMemo<InventoryCategoryGroup[]>(() => {
    const groups = new Map<string, InventoryCategoryGroup>();

    filteredInventory.forEach((row) => {
      const product = row.productVariant?.product;
      const key = product?.productCategoryId || 'uncategorized';
      const group = groups.get(key) || {
        key,
        name: product?.productCategoryName
          ? formatCategoryPath(product.productCategoryName, product.productCategoryParentName)
          : t('inventory:categories.uncategorized'),
        color: product?.productCategoryColor || null,
        rows: [],
        inStockCount: 0,
        outOfStockCount: 0,
        inTransitCount: 0,
      };

      group.rows.push(row);
      if (deriveStockStatus(row) === 'in') group.inStockCount += 1;
      if (deriveStockStatus(row) === 'out') group.outOfStockCount += 1;
      if (
        hasPersistedQuantitySet(row as unknown as Record<string, unknown>, 'quantityInTransit')
          ? hasPositiveQuantitySet(row, 'quantityInTransit')
          : Number(row.quantityInTransit || 0) > 0
      ) group.inTransitCount += 1;
      groups.set(key, group);
    });

    return Array.from(groups.values())
      .map(group => ({
        ...group,
        rows: [...group.rows].sort((a, b) => {
          const productCompare = (a.productVariant?.product?.name || '').localeCompare(
            b.productVariant?.product?.name || '', i18n.language,
          );
          if (productCompare !== 0) return productCompare;
          return (a.productVariant?.variantName || '').localeCompare(
            b.productVariant?.variantName || '', i18n.language,
          );
        }),
      }))
      .sort((a, b) => {
        if (a.key === 'uncategorized') return 1;
        if (b.key === 'uncategorized') return -1;
        return a.name.localeCompare(b.name, i18n.language);
      });
  }, [filteredInventory, i18n.language, t]);

  const hasActiveFilters = Boolean(filters.search || filters.status || filters.lowStock);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const toggleLowStockFilter = () => {
    setFilters(prev => ({ ...prev, lowStock: !prev.lowStock }));
  };

  const toggleOutOfStockFilter = () => {
    setFilters(prev => ({ ...prev, status: prev.status === 'OUT_OF_STOCK' ? null : 'OUT_OF_STOCK' }));
  };

  const renderQuantitySetText = (
    record: object,
    prefix: string,
    legacyQuantity: number,
    absolute = false,
  ) => {
    return formatQuantitySet(
      record as Record<string, unknown>,
      prefix,
      i18n.language,
      {
        absolute,
        fallbackQuantity: legacyQuantity,
        fallbackUnit: (record as { quantityUnit?: Inventory['quantityUnit'] }).quantityUnit,
      },
    );
  };

  const renderAvailable = (qty: number, record: InventoryWithDetails) => {
    const status = deriveStockStatus(record);
    const tone = status === 'in' ? 'ok' : status;

    return (
      <span className={`inventory-dashboard__qty inventory-dashboard__qty--${tone}`}>
        <span className="inventory-dashboard__dot" aria-hidden="true" />
        <span className="inventory-dashboard__quantity-lines">{renderQuantitySetText(record, 'quantityAvailable', qty).split(' / ').map((value, index) => <span key={index}>{value}</span>)}</span>
      </span>
    );
  };

  const renderStatusPill = (record: InventoryWithDetails) => {
    const status = deriveStockStatus(record);
    const labels: Record<StockStatus, string> = {
      in: t('inventory:status.inStock'),
      low: t('inventory:status.lowStock'),
      out: t('inventory:status.outOfStock'),
    };
    return <span className={`inventory-dashboard__status inventory-dashboard__status--${status}`}>{labels[status]}</span>;
  };

  const firstQuantityPart = (record: InventoryWithDetails, prefix: string, legacy: number) => (
    renderQuantitySetText(record, prefix, legacy || 0).split(' / ')[0]
  );

  // Every unit of the quantity set, built only when the pointer reaches the
  // bar: it is hidden detail and costs two formatting passes per row (640+
  // rows on staging).
  const showStockBarTitle = (event: React.MouseEvent<HTMLDivElement>, record: InventoryWithDetails) => {
    event.currentTarget.title = [
      `${t('inventory:stockBar.reserved')}: ${renderQuantitySetText(record, 'quantityReserved', record.quantityReserved || 0)}`,
      `${t('inventory:stockBar.inTransit')}: ${renderQuantitySetText(record, 'quantityInTransit', record.quantityInTransit || 0)}`,
    ].join('\n');
  };

  // Free / reserved / in-transit as shares of one unit; the legend shows the
  // primary unit, the hover title carries every unit of the quantity set.
  const renderStockBar = (record: InventoryWithDetails) => {
    const shares = getStockBarShares(record as unknown as Record<string, unknown>);
    const reservedText = firstQuantityPart(record, 'quantityReserved', record.quantityReserved);
    const inTransitText = firstQuantityPart(record, 'quantityInTransit', record.quantityInTransit);
    const total = shares ? shares.free + shares.reserved + shares.inTransit : 0;
    return (
      <div className="inventory-dashboard__stockbar" onMouseEnter={(event) => showStockBarTitle(event, record)}>
        <div className="inventory-dashboard__stockbar-track" aria-hidden="true">
          {shares && total > 0 && (['free', 'reserved', 'inTransit'] as const).map((key) => (
            shares[key] > 0 && (
              <i
                key={key}
                className={`inventory-dashboard__stockbar-seg inventory-dashboard__stockbar-seg--${key}`}
                style={{ width: `${(shares[key] / total) * 100}%` }}
              />
            )
          ))}
        </div>
        <div className="inventory-dashboard__stockbar-legend">
          <span>{reservedText} {t('inventory:stockBar.reservedShort')}</span>
          <span>{inTransitText} {t('inventory:stockBar.inTransitShort')}</span>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: t('inventory:fields.productName'),
      key: 'productName',
      render: (record: InventoryWithDetails) => (
        <div className="inventory-dashboard__product">
          <div className="inventory-dashboard__product-name">{record.productVariant?.product?.name}</div>
          <div className="inventory-dashboard__product-meta">{record.productVariant?.variantName}</div>
        </div>
      ),
    },
    {
      title: t('inventory:fields.dimensions'),
      key: 'dimensions',
      width: 118,
      render: (record: InventoryWithDetails) => (
        <ProductDimensionsStack product={record.productVariant?.product} />
      ),
    },
    {
      title: t('inventory:fields.stockBar'),
      key: 'stockBar',
      width: 210,
      render: (_: unknown, record: InventoryWithDetails) => renderStockBar(record),
    },
    {
      title: t('inventory:fields.quantityAvailable'),
      dataIndex: 'quantityAvailable',
      key: 'quantityAvailable',
      width: 125,
      align: 'right' as const,
      render: (qty: number, record: InventoryWithDetails) => (
        <div className="inventory-dashboard__available">
          {renderAvailable(qty, record)}
          {renderStatusPill(record)}
        </div>
      ),
    },
  ];

  // Expanded row to show batches and movements
  const expandedRowRender = (record: InventoryWithDetails) => {
    const variantId = record.productVariantId;
    const detail = variantDetails[variantId];

    if (!detail || detail.loading) {
      return (
        <div className="inventory-dashboard__loading">
          <Space direction="vertical">
            <Spin />
            <Text type="secondary">{t('inventory:details.loading')}</Text>
          </Space>
        </div>
      );
    }

    if (detail.error) {
      return (
        <Alert
          type="error"
          showIcon
          message={t('inventory:details.errorTitle')}
          description={detail.error}
          action={(
            <Button size="small" onClick={() => void fetchVariantDetails(variantId)}>
              {t('common:retry')}
            </Button>
          )}
        />
      );
    }

    const variantBatches = detail.batches;
    const variantMovements = detail.movements;

    // Earmark columns for the per-batch "reserved for orders" sub-table.
    // Uses inventory:earmarks.* keys that were pre-provisioned in Ticket C.
    const earmarkColumns = [
      {
        title: t('inventory:earmarks.colOrder'),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
      },
      {
        title: t('inventory:earmarks.colQty'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (qty: number, earmark: BatchEarmark) => (
          renderQuantitySetText(earmark, 'quantity', qty)
        ),
      },
      {
        title: t('inventory:earmarks.colConsumed'),
        dataIndex: 'consumedQuantity',
        key: 'consumedQuantity',
        render: (qty: number, earmark: BatchEarmark) => (
          renderQuantitySetText(earmark, 'consumedQuantity', qty)
        ),
      },
      {
        title: t('inventory:earmarks.colStatus'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'ACTIVE' ? 'blue' : status === 'FULFILLED' ? 'green' : 'default'}>
            {t(`inventory:earmarks.status_${status}`)}
          </Tag>
        ),
      },
      {
        title: t('inventory:earmarks.colEarmarkedAt'),
        dataIndex: 'earmarkedAt',
        key: 'earmarkedAt',
        render: (date: string) => formatDate(date),
      },
    ];

    // Expanded sub-row for a single batch — shows active earmarks (orders that
    // reserved units from this batch). Loads on first expand; subsequent expands
    // use the cached result from batchEarmarks state.
    const batchExpandedRowRender = (batch: InventoryBatch) => {
      const earmarks = batchEarmarks[batch.id];
      // Filter to ACTIVE earmarks only — fulfilled/released are noise in this view
      const activeEarmarks = earmarks
        ? earmarks.filter((e: BatchEarmark) => e.status === 'ACTIVE')
        : undefined;

      return (
        <div className="inventory-dashboard__earmarks">
          <div className="inventory-dashboard__earmarks-title">
            {t('inventory:earmarks.title')}
          </div>
          <Table
            columns={earmarkColumns}
            dataSource={activeEarmarks}
            loading={earmarks === undefined}
            pagination={false}
            rowKey="id"
            size="small"
            locale={{ emptyText: t('inventory:earmarks.noData') }}
          />
        </div>
      );
    };

    const direction = (movement: InventoryMovement): 'in' | 'out' | 'neutral' => {
      if (['PZ_IN', 'ADJUSTMENT_IN', 'RETURN'].includes(movement.movementType)) return 'in';
      if (['WZ_OUT', 'ADJUSTMENT_OUT', 'DAMAGE'].includes(movement.movementType)) return 'out';
      const before = quantitySetFromFields(movement as unknown as Record<string, unknown>, 'quantityBefore');
      const after = quantitySetFromFields(movement as unknown as Record<string, unknown>, 'quantityAfter');
      const unit = (['pcs', 'm2', 'm3'] as const).find(key => before[key] != null && after[key] != null);
      const difference = unit ? after[unit]! - before[unit]! : movement.quantityAfter - movement.quantityBefore;
      return difference > 0 ? 'in' : difference < 0 ? 'out' : 'neutral';
    };
    type TimelineEvent = {
      key: string;
      date: string;
      direction: 'in' | 'out' | 'neutral';
    } & ({ kind: 'batch'; batch: InventoryBatch; movement?: never } | { kind: 'movement'; movement: InventoryMovement; batch?: never });

    // A PZ movement and its received batch describe the same receipt. Keep the
    // batch (including its remaining stock and reservations) as that event.
    const events: TimelineEvent[] = [
      ...variantBatches.map(batch => ({
        kind: 'batch' as const,
        key: `batch-${batch.id}`,
        date: batch.receiptDate,
        direction: 'in' as const,
        batch,
      })),
      ...variantMovements
        .filter(movement => !(movement.movementType === 'PZ_IN' && variantBatches.some(batch => (
          batch.pzDocument?.pzNumber && batch.pzDocument.pzNumber === movement.referenceNumber
        ))))
        .map(movement => ({
          kind: 'movement' as const,
          key: `movement-${movement.id}`,
          date: movement.movementDate,
          direction: direction(movement),
          movement,
        })),
    ];
    const timestamp = (date: string) => {
      const value = Date.parse(date);
      return Number.isNaN(value) ? -Infinity : value;
    };
    events.sort((left, right) => timestamp(right.date) - timestamp(left.date));

    return (
      <div className="inventory-dashboard__expanded">
        <div className="inventory-dashboard__timeline-heading" aria-hidden="true">
          <span className="inventory-dashboard__timeline-out">− {t('inventory:tree.outbound')}</span>
          <span className="inventory-dashboard__timeline-order">{t('inventory:tree.newestFirst')}</span>
          <span className="inventory-dashboard__timeline-in">+ {t('inventory:tree.inbound')}</span>
        </div>
        <p className="inventory-dashboard__timeline-hint">{t('inventory:tree.scope')}</p>
        <ol className="inventory-dashboard__timeline" aria-label={t('inventory:tree.title')}>
          {events.map(event => {
            const batch = event.batch;
            const reference = event.kind === 'batch'
              ? event.batch.pzDocument?.pzNumber
              : event.movement.referenceNumber;
            const label = event.kind === 'batch'
              ? t('inventory:movementType.pz_in')
              : event.movement.reason === 'COMPLAINT_WRITE_OFF'
                ? t('inventory:movementType.complaint_write_off')
                : t(`inventory:movementType.${event.movement.movementType.toLowerCase()}`, { defaultValue: t('inventory:tree.other') });
            const sideLabel = t(`inventory:tree.${event.direction === 'in' ? 'inbound' : event.direction === 'out' ? 'outbound' : 'other'}`);
            return (
              <li key={event.key} className={`inventory-dashboard__event inventory-dashboard__event--${event.direction}`}>
                <div className="inventory-dashboard__event-axis">
                  <span className="inventory-dashboard__event-dot" aria-hidden="true" />
                  <time>{formatDate(event.date)}</time>
                </div>
                <article className="inventory-dashboard__event-content" aria-label={`${sideLabel} · ${reference || label}`}>
                  <div className="inventory-dashboard__event-title">
                    <span>{label}</span>
                    <span>{reference || t('inventory:tree.noDocument')}</span>
                  </div>
                  <div className="inventory-dashboard__event-quantity">
                    <span className="inventory-dashboard__event-sign" aria-hidden="true">{event.direction === 'in' ? '+' : event.direction === 'out' ? '−' : '·'}</span>
                    {event.kind === 'batch'
                      ? renderQuantitySetText(event.batch, 'quantityReceived', event.batch.quantityReceived, true)
                      : renderQuantitySetText(event.movement, 'quantity', event.movement.quantity, true)}
                  </div>
                  {batch && (
                    <>
                      <dl className="inventory-dashboard__batch-values">
                        <div><dt>{t('inventory:batchFields.quantityRemaining')}</dt><dd>{renderQuantitySetText(batch, 'quantityRemaining', batch.quantityRemaining)}</dd></div>
                        {canViewPurchasePrices && <div><dt>{t('inventory:batchFields.unitCost')}</dt><dd>{batch.unitCostPLN == null ? EMPTY_QTY : formatCurrency(batch.unitCostPLN, 'PLN')}</dd></div>}
                        <div><dt>{t('inventory:batchFields.status')}</dt><dd>{t(`inventory:batchStatus.${batch.status.toLowerCase()}`, { defaultValue: t('inventory:tree.unknownStatus') })}</dd></div>
                      </dl>
                      <details aria-label={t('inventory:earmarks.title')} onToggle={event => { if (event.currentTarget.open) void fetchEarmarksForBatch(batch.id); }}>
                        <summary>{t('inventory:earmarks.title')}</summary>
                        {batchExpandedRowRender(batch)}
                      </details>
                    </>
                  )}
                </article>
              </li>
            );
          })}
        </ol>
        {!events.length && <p className="inventory-dashboard__timeline-hint">{t('inventory:noMovements')}</p>}
      </div>
    );
  };

  return (
    <div className="app-page inventory-dashboard-page">
      <div className="inventory-dashboard__kpis">
        <div className="inventory-dashboard__kpi">
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.totalItems')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.totalItems}</span>
        </div>
        {canViewPurchasePrices && (
          <div className="inventory-dashboard__kpi">
            <span className="inventory-dashboard__kpi-label">{t('inventory:summary.totalValue')}</span>
            <span className="inventory-dashboard__kpi-value">{formatCurrency(summary.totalValue, 'PLN')}</span>
          </div>
        )}
        <button
          type="button"
          className={`inventory-dashboard__kpi inventory-dashboard__kpi--interactive inventory-dashboard__kpi--warning${summary.lowStockCount > 0 ? ' has-value' : ''}${filters.lowStock ? ' is-active' : ''}`}
          aria-pressed={filters.lowStock}
          onClick={toggleLowStockFilter}
        >
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.lowStock')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.lowStockCount}</span>
        </button>
        <button
          type="button"
          className={`inventory-dashboard__kpi inventory-dashboard__kpi--interactive inventory-dashboard__kpi--danger${summary.outOfStockCount > 0 ? ' has-value' : ''}${filters.status === 'OUT_OF_STOCK' ? ' is-active' : ''}`}
          aria-pressed={filters.status === 'OUT_OF_STOCK'}
          onClick={toggleOutOfStockFilter}
        >
          <span className="inventory-dashboard__kpi-label">{t('inventory:summary.outOfStock')}</span>
          <span className="inventory-dashboard__kpi-value">{summary.outOfStockCount}</span>
        </button>
      </div>

      {/* Flat toolbar — frontend-pages.md / Products.tsx reference pattern. */}
      <div className="app-page__toolbar inventory-dashboard__toolbar">
        <Input
          className="inventory-dashboard__search"
          placeholder={t('inventory:filters.search')}
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          allowClear
        />
        <Space>
          <Select
            placeholder={t('inventory:filters.status')}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            allowClear
            style={{ width: 190 }}
          >
            <Option value="ACTIVE">{t('inventory:status.inStock')}</Option>
            <Option value="LOW_STOCK">{t('inventory:status.lowStock')}</Option>
            <Option value="OUT_OF_STOCK">{t('inventory:status.outOfStock')}</Option>
          </Select>
          <Button
            type={filters.lowStock ? 'primary' : 'default'}
            icon={<WarningOutlined />}
            onClick={toggleLowStockFilter}
          >
            {t('inventory:filters.lowStockOnly')}
          </Button>
          {hasActiveFilters && (
            <Button type="link" onClick={clearFilters}>
              {t('common:clearFilters')}
            </Button>
          )}
          {(canViewPurchasePrices || canViewBatchProfitability) && (
            <Dropdown menu={{ items: [
              ...(canViewBatchProfitability ? [
                { key: 'batch-ledger', label: t('inventory:actions.viewBatchLedger'), onClick: () => navigate('/app/inventory/batch-ledger') },
                { key: 'batch-profitability', label: t('inventory:actions.viewBatchProfitability'), onClick: () => navigate('/app/inventory/batch-profitability') },
              ] : []),
              ...(canViewPurchasePrices ? [{ key: 'reports', label: t('inventory:actions.viewReports'), onClick: () => navigate('/app/inventory/reports') }] : []),
            ] }} trigger={['click']}>
              <Button icon={<BarChartOutlined />}>{t('inventory:actions.viewReports')} <DownOutlined /></Button>
            </Dropdown>
          )}
        </Space>
      </div>

      <div className="inventory-dashboard__groups" aria-busy={loading}>
        {groupedInventory.map((group) => (
          <Collapse
            key={group.key}
            className="app-page__card inventory-dashboard__category"
            bordered={false}
            defaultActiveKey={['inventory']}
            expandIconPosition="end"
            expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
            items={[{
              key: 'inventory',
              label: (
                <div className="inventory-dashboard__category-header">
                  <div className="inventory-dashboard__category-identity">
                    <span
                      className="inventory-dashboard__category-mark"
                      style={{ [CATEGORY_MARK_VAR as any]: group.color || undefined } as React.CSSProperties}
                      aria-hidden="true"
                    />
                    <span className="inventory-dashboard__category-name">{group.name}</span>
                    <span className="inventory-dashboard__category-count">
                      {t('inventory:categories.itemCount', { count: group.rows.length })}
                    </span>
                  </div>
                  <div className="inventory-dashboard__category-totals" aria-hidden="true">
                    <span><b>{group.inStockCount}</b>{t('inventory:categories.inStock')}</span>
                    <span><b>{group.outOfStockCount}</b>{t('inventory:categories.outOfStock')}</span>
                    <span><b>{group.inTransitCount}</b>{t('inventory:categories.inTransit')}</span>
                  </div>
                </div>
              ),
              children: (
                <Table
                  className="inventory-dashboard__table"
                  size="small"
                  dataSource={group.rows}
                  columns={columns}
                  loading={loading}
                  rowKey="productVariantId"
                  scroll={{ x: 920 }}
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: handleExpand,
                  }}
                  pagination={false}
                />
              ),
            }]}
          />
        ))}
        {!loading && groupedInventory.length === 0 && (
          <div className="inventory-dashboard__empty">{t('inventory:categories.noMatches')}</div>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboard;
